import type { Customer, Store } from "@/services/features/StoreService";
import { Region } from "@/utils/environment";

import type { StoreHealthCache } from "./commandCenter.types";
import { getStoreHealth } from "./commandCenterHealth";

// Reuse sanitized entries between checkpoints; each completed store is immutable.
const normalizedHealth = new WeakMap<StoreHealthCache, StoreHealthCache>();

export const COMMAND_CENTER_CACHE_VERSION = 3;
export const COMMAND_CENTER_CACHE_TTL_MS = 5 * 60 * 1000;

export type CommandCenterDataSnapshot = {
  customers: Customer[];
  stores: Store[];
  healthByStoreId: Record<number, StoreHealthCache>;
};

type CommandCenterSessionCache = CommandCenterDataSnapshot & {
  version: number;
  createdAt: number;
};

export type CommandCenterCacheScope = { region: Region; userId?: number };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isCompleteHealthCache = (value: unknown) =>
  isRecord(value) &&
  value.state === "complete" &&
  typeof value.completedAt === "number" &&
  Array.isArray(value.devices) &&
  isRecord(value.health);

const hasOnlyCompleteHealth = (healthByStoreId: unknown) =>
  isRecord(healthByStoreId) && Object.values(healthByStoreId).every(isCompleteHealthCache);

export const getCommandCenterCacheKey = ({ region, userId }: CommandCenterCacheScope) =>
  `commandCenter.data.v${COMMAND_CENTER_CACHE_VERSION}.${region}.${userId ?? "anonymous"}`;

const isValidSnapshot = (value: unknown, now: number): value is CommandCenterSessionCache => {
  if (!isRecord(value)) return false;

  return (
    value.version === COMMAND_CENTER_CACHE_VERSION &&
    typeof value.createdAt === "number" &&
    value.createdAt <= now &&
    now - value.createdAt < COMMAND_CENTER_CACHE_TTL_MS &&
    Array.isArray(value.customers) &&
    Array.isArray(value.stores) &&
    hasOnlyCompleteHealth(value.healthByStoreId)
  );
};

export const readCommandCenterSessionCache = (
  scope: CommandCenterCacheScope,
  now = Date.now(),
): CommandCenterDataSnapshot | null => {
  if (typeof window === "undefined") return null;

  const key = getCommandCenterCacheKey(scope);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const cached: unknown = JSON.parse(raw);
    if (!isValidSnapshot(cached, now)) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return {
      customers: cached.customers,
      stores: cached.stores,
      healthByStoreId: cached.healthByStoreId,
    };
  } catch {
    return null;
  }
};

export const writeCommandCenterSessionCache = (
  scope: CommandCenterCacheScope,
  snapshot: CommandCenterDataSnapshot,
  createdAt?: number,
) => {
  if (typeof window === "undefined") return;

  try {
    const now = Date.now();
    const raw = window.sessionStorage.getItem(getCommandCenterCacheKey(scope));
    const existing: unknown = raw ? JSON.parse(raw) : null;
    const snapshotCreatedAt =
      createdAt ?? (isValidSnapshot(existing, now) ? existing.createdAt : now);
    const storeById = new Map(snapshot.stores.map((store) => [store.id, store]));
    const healthByStoreId = Object.fromEntries(
      Object.entries(snapshot.healthByStoreId)
        .filter(([, entry]) => entry.state === "complete" && entry.devices && entry.health)
        .map(([storeId, entry]) => {
          const cached = normalizedHealth.get(entry);
          if (cached) return [storeId, cached];

          // The loader already separates ignored devices and computes health. Only
          // repair legacy/unsanitized entries, rather than recomputing every store.
          const devices = entry.devices ?? [];
          const needsFiltering = devices.some((device) => device.ignored);
          const reportableDevices = needsFiltering
            ? devices.filter((device) => !device.ignored)
            : devices;
          const normalized = {
            ...entry,
            devices: reportableDevices,
            ignoredDevices: (entry.ignoredDevices ?? []).filter((device) => device.ignored),
            health: needsFiltering
              ? getStoreHealth(reportableDevices, storeById.get(Number(storeId))?.timezone ?? "UTC")
              : entry.health,
          };
          normalizedHealth.set(entry, normalized);
          return [storeId, normalized];
        }),
    );
    const cache: CommandCenterSessionCache = {
      ...snapshot,
      healthByStoreId,
      version: COMMAND_CENTER_CACHE_VERSION,
      createdAt: snapshotCreatedAt,
    };
    window.sessionStorage.setItem(getCommandCenterCacheKey(scope), JSON.stringify(cache));
  } catch {
    // Storage can be unavailable or full; Command Center continues without a persisted cache.
  }
};

export const clearCommandCenterSessionCache = (scope: CommandCenterCacheScope) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(getCommandCenterCacheKey(scope));
  } catch {
    // Storage can be unavailable; there is no cache to clear in that case.
  }
};
